import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface UserRole {
  id: number;
  name: string;
}

interface User {
  id: number;
  username: string;
  name: string;
  roles: UserRole[];
}

export interface RegistrationData {
  registrationToken: string;
  needsQualification: boolean;
  nexusData: {
    nexusId?: string;
    firstName: string;
    lastName: string;
    email: string;
    countyName: string;
    type: string;
  } | null;
}

interface AuthContextType {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  registrationData: RegistrationData | null;
  login: (accessToken: string, refreshToken: string, user: User) => void;
  logout: () => Promise<void>;
  clearSession: () => Promise<void>;
  refreshAccessToken: () => Promise<string | null>;
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  hasRole: (roleName: string) => boolean;
  setRegistrationData: (data: RegistrationData | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const USER_KEY = 'user';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [registrationData, setRegistrationData] = useState<RegistrationData | null>(null);

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    const storedAccessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const storedUser = localStorage.getItem(USER_KEY);

    if (storedAccessToken && storedUser) {
      setAccessToken(storedAccessToken);
      setUser(JSON.parse(storedUser));
    }

    setIsLoading(false);
  }, []);

  const login = (newAccessToken: string, refreshToken: string, newUser: User) => {
    setAccessToken(newAccessToken);
    setUser(newUser);

    localStorage.setItem(ACCESS_TOKEN_KEY, newAccessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(newUser));
  };

  const clearLocalSession = () => {
    setAccessToken(null);
    setUser(null);

    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem('marketerLeadType');
    localStorage.removeItem('marketerActiveForm');
    localStorage.removeItem('marketerCampaignId');
  };

  const invalidateServerSession = async (token: string) => {
    await fetch('/api/auth/logout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  };

  const logout = async () => {
    try {
      // Call logout endpoint to invalidate refresh token
      if (accessToken) {
        await invalidateServerSession(accessToken);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local state regardless of API success
      clearLocalSession();
    }
  };

  // Drop the stored session right away (e.g. another person's login link was opened
  // on this browser). Resolves once the server-side invalidation has finished, so a new
  // login for the same user is not wiped out by this logout landing after it.
  const clearSession = () => {
    const serverLogout = accessToken ? invalidateServerSession(accessToken) : Promise.resolve();
    clearLocalSession();
    return serverLogout.catch((error) => console.error('Logout error:', error));
  };

  const refreshAccessToken = async (): Promise<string | null> => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

    if (!refreshToken) {
      await logout();
      return null;
    }

    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        // Refresh token invalid - logout
        await logout();
        return null;
      }

      const data = await response.json();

      setAccessToken(data.accessToken);
      localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);

      return data.accessToken;
    } catch (error) {
      console.error('Token refresh error:', error);
      await logout();
      return null;
    }
  };

  const hasRole = (roleName: string): boolean => {
    return user?.roles?.some((r) => r.name === roleName) ?? false;
  };

  const authFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const injectAuth = (token: string | null, opts?: RequestInit): RequestInit => {
      const headers = new Headers(opts?.headers);
      if (token) headers.set('Authorization', `Bearer ${token}`);
      return { ...opts, headers };
    };

    let res = await fetch(input, injectAuth(accessToken, init));

    if (res.status === 401) {
      const refreshedToken = await refreshAccessToken();
      if (refreshedToken) {
        res = await fetch(input, injectAuth(refreshedToken, init));
      }
    }

    return res;
  };

  const value: AuthContextType = {
    user,
    accessToken,
    isAuthenticated: !!accessToken && !!user,
    isLoading,
    registrationData,
    login,
    logout,
    clearSession,
    refreshAccessToken,
    authFetch,
    hasRole,
    setRegistrationData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
